import React, { useState } from "react"
import {
  Col,
  Row,
  Button,
  TabPane,
  Card,
  CardBody,
  Table,
  Alert,
  Form,
  FormGroup,
  Label,
  Input,
  Progress,
} from "reactstrap"
import { Link, useNavigate } from "react-router-dom"
import {
  CreateCPSRequest,
  GetCPSRequestByID,
  UpdateCPSRequest,
  UploadCPSAttachment,
} from "helpers/backend_helper"

const CPSConfirmationTab = ({ request, setRequest, previousTab }) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [submittedRequestId, setSubmittedRequestId] = useState(null)
  const [isUploadingInstruction, setIsUploadingInstruction] = useState(false)
  const [instructionUploadProgress, setInstructionUploadProgress] = useState(0)
  const [instructionFile, setInstructionFile] = useState(null)
  const [instructionUploaded, setInstructionUploaded] = useState(false)
  const [instructionURL, setInstructionURL] = useState(null)
  const navigate = useNavigate()

  const handleSubmit = async () => {
    setIsSubmitting(true)

    try {
      // Transform frontend request data to API format
      const apiRequest = {
        type: request.type,
        articleDescription: request.articleDescription,
        customerOrderNumber: request.customerOrderNumber || "",
        mpn: request.mpn,
        manufacturer: request.manufacturer,
        packageType: request.packageType,
        deliveryForm: request.deliveryForm,
        supplyForm: request.supplyForm,
        needType: request.needType === "one-time" ? "one-time" : "annual",
        quantity: parseInt(request.quantity),
        safetyStockMin: request.safetyStockMin
          ? parseInt(request.safetyStockMin)
          : null,
        safetyStockMax: request.safetyStockMax
          ? parseInt(request.safetyStockMax)
          : null,
        sampleRequired: request.sampleRequired ? 1 : 0,
        sampleQuantity: request.sampleQuantity
          ? parseInt(request.sampleQuantity)
          : null,
        samplePrice: request.samplePrice
          ? parseFloat(request.samplePrice)
          : null,
        sampleDeliveryDate: request.sampleDeliveryDate || null,
        desiredDeliveryDate: request.desiredDeliveryDate || null,
        statusIndex: 1, // Request Created
        priority: "Medium", // Default priority
        customerSupply: 1, // Always true for CPS
        supplier: null, // Not applicable for CPS
        price: null, // Will be set by CPS team
        deliveryTime: null, // Will be set by CPS team
        currency: "EUR", // Default currency
        instructions: request.instructions || "",
        instructionURL: request.instructionURL || null,
        sampleTrackingNumber: request.sampleTrackingNumber || null,
        sampleShippingService: request.sampleShippingService || null,
        notes: request.notes || "",
        isParentRequest: request.isSubRequest ? 0 : 0, // Sub-requests are never parent requests
        parentID: request.isSubRequest ? request.parentID : null, // Parent ID for linking
        parentRequestNumber: request.isSubRequest
          ? request.parentRequestNumber
          : null,
        orderNumber: null, // Will be generated by backend
        cpsLabID: 1, // Default CPS lab ID - should be dynamic in real implementation
      }

      const result = await CreateCPSRequest(apiRequest)
      const newEntry = await GetCPSRequestByID(result.id)

      // If there's a file to upload, do it now
      if (request.fileUpload) {
        try {
          setIsUploadingInstruction(true)
          setInstructionUploadProgress(0)

          const formData = new FormData()
          formData.append("file", request.fileUpload)
          formData.append("id", result.id)

          // Simulate progress
          const progressInterval = setInterval(() => {
            setInstructionUploadProgress(prev => Math.min(prev + 10, 90))
          }, 200)

          const uploadResult = await UploadCPSAttachment(formData)

          clearInterval(progressInterval)
          setInstructionUploadProgress(100)

          newEntry.instructionURL = uploadResult?.url

          await UpdateCPSRequest(newEntry)
          setInstructionURL(uploadResult?.url)
          setInstructionUploaded(true)
        } catch (uploadError) {
          console.error("Error uploading instruction file:", uploadError)
          // Continue even if upload fails
        } finally {
          setIsUploadingInstruction(false)
        }
      }

      setIsSubmitting(false)
      setIsSubmitted(true)
      setSubmittedRequestId(result.id)
    } catch (error) {
      console.error("Error creating CPS request:", error)
      setIsSubmitting(false)
      // You might want to show an error message to the user here
    }
  }

  if (isSubmitted) {
    return (
      <TabPane tabId={4}>
        <div className="text-center">
          <div className="avatar-md mx-auto mb-4">
            <div className="avatar-title bg-light rounded-circle text-success font-size-24">
              <i className="bx bx-check"></i>
            </div>
          </div>
          <div className="p-2">
            <h4>Request Submitted Successfully!</h4>
            <p className="text-muted">
              Your CPS {request.type} request has been submitted and will be
              reviewed by our team. You will receive an offer within 24-48
              hours.
            </p>
            <p className="text-muted mb-4">
              Request Number:{" "}
              <strong>
                {submittedRequestId
                  ? `CPS-${submittedRequestId}`
                  : "Processing..."}
              </strong>
            </p>

            {/* Instruction Upload Status */}
            {request.fileUpload && (
              <div className="mt-4">
                {isUploadingInstruction ? (
                  <div className="alert alert-info">
                    <div className="d-flex align-items-center">
                      <div
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      >
                        <span className="visually-hidden">Uploading...</span>
                      </div>
                      <span>Uploading instruction file...</span>
                    </div>
                    <Progress
                      value={instructionUploadProgress}
                      className="mt-2"
                      color="info"
                    />
                  </div>
                ) : instructionUploaded ? (
                  <div className="alert alert-success">
                    <i className="bx bx-check-circle me-2"></i>
                    Instruction file uploaded successfully!
                    {instructionURL && (
                      <div className="mt-2">
                        <a
                          href={instructionURL}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline-success"
                        >
                          <i className="bx bx-link-external me-1"></i>
                          View Instructions
                        </a>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="alert alert-warning">
                    <i className="bx bx-error me-2"></i>
                    Instruction file upload failed. You can upload it later from
                    the request details.
                  </div>
                )}
              </div>
            )}
            <div className="mt-4">
              <Link
                to="/cps-requests"
                className="btn btn-primary waves-effect waves-light me-2"
              >
                View All Requests
              </Link>
              {/* <Link
                to="/cps-requests/4"
                className="btn btn-primary btn-rounded waves-effect waves-light"
              >
                View Request Details
              </Link> */}
            </div>
          </div>
        </div>
      </TabPane>
    )
  }

  return (
    <TabPane tabId={4}>
      <div>
        <div className="text-center mb-4">
          <h5 className="card-title">Confirm Your Request</h5>
          <p className="card-title-desc">
            Please review your request details before submitting
          </p>
        </div>

        <Row className="justify-content-center">
          <Col lg="10">
            <Card>
              <CardBody>
                <h6 className="card-title">Request Summary</h6>

                <Table className="table-borderless mb-0">
                  <tbody>
                    <tr>
                      <td className="fw-medium" style={{ width: "30%" }}>
                        Service Type:
                      </td>
                      <td>{request.type}</td>
                    </tr>
                    <tr>
                      <td className="fw-medium">Article Description:</td>
                      <td>{request.articleDescription}</td>
                    </tr>
                    {request.customerOrderNumber && (
                      <tr>
                        <td className="fw-medium">Customer Order Number:</td>
                        <td>{request.customerOrderNumber}</td>
                      </tr>
                    )}
                    <tr>
                      <td className="fw-medium">MPN:</td>
                      <td>{request.mpn}</td>
                    </tr>
                    <tr>
                      <td className="fw-medium">Manufacturer:</td>
                      <td>{request.manufacturer}</td>
                    </tr>
                    <tr>
                      <td className="fw-medium">Package Type:</td>
                      <td>{request.packageType}</td>
                    </tr>
                    <tr>
                      <td className="fw-medium">Delivery Form:</td>
                      <td className="text-capitalize">
                        {request.deliveryForm?.replace("-", " ")}
                      </td>
                    </tr>
                    <tr>
                      <td className="fw-medium">Supply Form:</td>
                      <td className="text-capitalize">
                        {request.supplyForm?.replace("-", " ")}
                      </td>
                    </tr>
                    <tr>
                      <td className="fw-medium">Need Type:</td>
                      <td className="text-capitalize">
                        {request.needType?.replace("-", " ")}
                      </td>
                    </tr>
                    <tr>
                      <td className="fw-medium">Quantity:</td>
                      <td>{request.quantity} pcs</td>
                    </tr>
                    {request.needType === "annual" &&
                      (request.safetyStockMin || request.safetyStockMax) && (
                        <tr>
                          <td className="fw-medium">Safety Stock:</td>
                          <td>
                            Min: {request.safetyStockMin || "Not set"} | Max:{" "}
                            {request.safetyStockMax || "Not set"}
                          </td>
                        </tr>
                      )}
                    <tr>
                      <td className="fw-medium">Customer Supply:</td>
                      <td>Yes (Always)</td>
                    </tr>
                    <tr>
                      <td className="fw-medium">Sample Required:</td>
                      <td>{request.sampleRequired ? "Yes" : "No"}</td>
                    </tr>
                    {request.sampleRequired && (
                      <>
                        <tr>
                          <td className="fw-medium">Sample Quantity:</td>
                          <td>{request.sampleQuantity} pcs</td>
                        </tr>
                        <tr>
                          <td className="fw-medium">Sample Delivery Date:</td>
                          <td>
                            {request.sampleDeliveryDate
                              ? new Date(
                                  request.sampleDeliveryDate
                                ).toLocaleDateString()
                              : "Not specified"}
                          </td>
                        </tr>
                      </>
                    )}
                    <tr>
                      <td className="fw-medium">Production Delivery Date:</td>
                      <td>
                        {request.desiredDeliveryDate
                          ? new Date(
                              request.desiredDeliveryDate
                            ).toLocaleDateString()
                          : "Not specified"}
                      </td>
                    </tr>
                    {request.fileUpload && (
                      <tr>
                        <td className="fw-medium">File Uploaded:</td>
                        <td>{request.fileUpload.name}</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </CardBody>
            </Card>

            {/* File Upload Section */}
            <Card className="mt-3">
              <CardBody>
                <h6 className="card-title">Instruction Files</h6>
                <p className="text-muted">
                  Upload programming files, configuration data, or sleeving
                  instructions.
                </p>

                <FormGroup>
                  <Label htmlFor="fileUpload">
                    Programming Info / Sleeving Instructions *
                  </Label>
                  <Input
                    type="file"
                    id="fileUpload"
                    onChange={e => {
                      const file = e.target.files[0]
                      if (file) {
                        setRequest({ ...request, fileUpload: file })
                      }
                    }}
                    accept=".pdf,.doc,.docx,.txt,.zip"
                    required
                  />
                  <div className="form-text text-muted">
                    Upload programming files, configuration data, or sleeving
                    instructions (required)
                  </div>
                </FormGroup>

                {request.fileUpload ? (
                  <div className="alert alert-success">
                    <i className="bx bx-file me-2"></i>
                    <strong>File selected:</strong> {request.fileUpload.name}
                    <div className="text-muted small mt-1">
                      Size: {(request.fileUpload.size / 1024 / 1024).toFixed(2)}{" "}
                      MB
                    </div>
                  </div>
                ) : (
                  <div className="alert alert-warning">
                    <i className="bx bx-error-circle me-2"></i>
                    Please upload an instruction file to continue. This file is required for processing your request.
                  </div>
                )}
              </CardBody>
            </Card>

            <Alert color="info">
              <i className="bx bx-info-circle me-2"></i>
              <strong>Next Steps:</strong> After submitting this request, our
              CPS team will review your requirements and provide a detailed
              offer including pricing, timeline, and any additional
              requirements.
            </Alert>
          </Col>
        </Row>
      </div>

      <div className="d-flex justify-content-between mt-4">
        <Button color="secondary" onClick={previousTab} disabled={isSubmitting}>
          Previous
        </Button>
        <Button
          color="primary"
          onClick={handleSubmit}
          disabled={isSubmitting || isUploadingInstruction || !request.fileUpload}
        >
          {isSubmitting ? (
            <>
              <i className="bx bx-loader bx-spin me-2"></i>
              Creating Request...
            </>
          ) : isUploadingInstruction ? (
            <>
              <i className="bx bx-loader bx-spin me-2"></i>
              Uploading Instructions...
            </>
          ) : (
            "Submit Request"
          )}
        </Button>
      </div>
    </TabPane>
  )
}

export default CPSConfirmationTab
